import { adminApiClient } from '@/services/admin/baseService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import {
  buildPaginatedResponse,
  extractArrayFromApiPayload,
} from '@/services/admin/paginatedResponseHelpers'
import type {
  ApiResponse,
  PaginatedResponse,
  SupportFormTemplate,
  SupportFormTemplateSchema,
  SupportReceive,
} from '@/types'

const normalizePaginatedResponse = <T>(
  payload: ApiResponse<any> | undefined,
  options: { keys?: string[]; defaultPage?: number; defaultLimit?: number } = {}
): PaginatedResponse<T> => {
  const payloadAny = payload as any
  const items = extractArrayFromApiPayload<T>(payloadAny, {
    keys: options.keys,
    candidateKeys: ['items', 'records', 'receives'],
    includePagination: true,
  })
  return buildPaginatedResponse(payload, items, options)
}

export interface SupportReceivesParams {
  page?: number
  limit?: number
  status?: string
  source?: string
  eventType?: string
  ticketId?: string
  sourceId?: string
}

export interface SupportTemplatePayload {
  slug: string
  name: string
  version?: number
  schema: SupportFormTemplateSchema
}

export const supportService = {
  async getSupportFormTemplate(mode?: NetworkEnvironment): Promise<SupportFormTemplate | null> {
    const response = await adminApiClient.get<ApiResponse<{ template?: SupportFormTemplate }>>(
      '/admin/support/ticket-template',
      buildModeAwareConfig(mode)
    )
    return response.data?.data?.template ?? null
  },

  async updateSupportFormTemplate(
    payload: SupportTemplatePayload
  ): Promise<SupportFormTemplate | null> {
    const response = await adminApiClient.put<ApiResponse<{ template?: SupportFormTemplate }>>(
      '/admin/support/ticket-template',
      payload
    )
    return response.data?.data?.template ?? null
  },

  async listSupportReceives(
    params?: SupportReceivesParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<SupportReceive>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.status) searchParams.append('status', params.status)
    if (params?.source) searchParams.append('source', params.source)
    if (params?.eventType) searchParams.append('eventType', params.eventType)
    if (params?.ticketId) searchParams.append('ticketId', params.ticketId)
    if (params?.sourceId) searchParams.append('sourceId', params.sourceId)

    const query = searchParams.toString()
    const url = query ? `/admin/support/receives?${query}` : '/admin/support/receives'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    return normalizePaginatedResponse<SupportReceive>(response.data, {
      keys: ['items'],
      defaultPage: params?.page,
      defaultLimit: params?.limit,
    })
  },

  async updateSupportReceive(
    recordId: string,
    payload: { ticketStatus?: string; supportNotes?: string | null }
  ): Promise<SupportReceive | null> {
    const response = await adminApiClient.put<ApiResponse<{ item?: SupportReceive }>>(
      `/admin/support/receives/${recordId}`,
      payload
    )
    return response.data?.data?.item ?? null
  },
}
